import { TFunction } from 'i18next';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { currentBrand /*idpHint*/ } from '../../config';
import { useNavigationBar } from './useNavigationBar';
import { useDashboardNavItem } from './useDashboardNavItem';

import {
    ItemClickCallback,
    NavigationBar as NavigationBarShared,
    SiteNavItemPropsList,
    SiteNavProps,
    SimpleNavItemProps,
    useAnalyticsActionTracker,
    useOidcAuthentication,
} from '@cp-shared-19/frontend-ui';

import {
    cookiePolicyPath,
    dashboardPagePath,
    faqPagePath,
    identificationPagePath,
    landingPagePath,
    legalNoticePath,
    loginInProgressPagePath,
    myProfilePagePath,
    myRequestPagePath,
    notAuthorizedPagePath,
    forbiddenPagePath,
    notFoundPagePath,
    postboxPagePath,
    registrationPagePath,
} from './paths';
import { useLogin } from 'auth';

const pagesWithoutNavbar = [
    registrationPagePath(),
    notAuthorizedPagePath(),
    identificationPagePath(),
    forbiddenPagePath(),
];

const pagesWithoutStaticHeader = [
    landingPagePath(),
    faqPagePath(),
    cookiePolicyPath(),
    legalNoticePath(),
    notAuthorizedPagePath(),
    notFoundPagePath(),
    registrationPagePath(),
];

export type NavigationBarProps = {
    isAuthenticated?: boolean;
};

const brandsMap = {
    audi: 'Audi',
    seat: 'Seat',
    skoda: 'Škoda',
    vw: 'Volkswagen',
    vwcv: 'VW Commercial Vehicles',
    df: 'DutchFinance',
};

function publicNavigationLinks(t: TFunction, login: ItemClickCallback): SiteNavItemPropsList {
    return [
        {
            href: registrationPagePath(),
            label: t('navigation.register'),
        },
        {
            label: t('navigation.login'),
            onClick: login,
            dropdownItems: [],
        },
    ];
}

function privateNavigationLinks(
    t: TFunction,
    logout: () => Promise<void>,
    dashboardItem: SimpleNavItemProps,
): SiteNavItemPropsList {
    return [
        dashboardItem,
        {
            href: myProfilePagePath(),
            url: myProfilePagePath(),
            label: t('navigation.profile'),
        },
        {
            href: myRequestPagePath(),
            url: myRequestPagePath(),
            label: t('navigation.my-request'),
        },
        {
            href: postboxPagePath(),
            url: postboxPagePath(),
            label: t('navigation.postbox'),
        },
        {
            href: faqPagePath(),
            url: faqPagePath(),
            label: t('navigation.faq'),
        },
        {
            label: t('navigation.logout'),
            onClick: logout,
            dropdownItems: [],
        },
    ];
}

export const NavigationBar: React.FC<NavigationBarProps> = () => {
    const { t } = useTranslation('navigation');
    const { isAuthenticated, logout } = useOidcAuthentication();
    const { cmsContent: navigationBar } = useNavigationBar();
    const currentPathName = useLocation().pathname;
    const history = useHistory();
    const location = useLocation();
    const login = useLogin(loginInProgressPagePath);
    const currentPageName = location.pathname.split('/')[1];
    const { onAction: onLogin } = useAnalyticsActionTracker('login');
    const { onAction: onLogout } = useAnalyticsActionTracker('logout');
    const dashboardItem = useDashboardNavItem(t);

    useEffect(() => {
        const toggle = document.querySelector<HTMLButtonElement>('.c-site-nav__toggle');
        if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
            toggle.click();
        }
    }, [currentPathName]);

    const navigationItems = (isAuthenticated
        ? privateNavigationLinks(
              t,
              () => {
                  onLogout(currentPageName);
                  return logout({ post_logout_redirect_uri: window.location.origin + landingPagePath() });
              },
              dashboardItem,
          )
        : publicNavigationLinks(t, () => {
              onLogin(currentPageName);
              return login();
          })
    ).map((navItem) => ({
        ...{ isActive: history.location.pathname === navItem.url },
        ...navItem,
    }));

    const hidden = pagesWithoutNavbar.includes(currentPathName);
    const withStaticHeader = !pagesWithoutStaticHeader.includes(currentPathName);
    const navigation: SiteNavProps = {
        navigationItems,
    };
    const NavigationBarSharedWithLogo =
        currentBrand === 'df' ? (
            <NavigationBarShared
                logoAltText={t('navigation.logo-title')}
                navigation={navigation}
                withStaticHeader={withStaticHeader}
                hidden={hidden}
                logoSource={navigationBar && navigationBar.logoHeaderUrl}
                logoClassName={'u-2/3 u-mt-small  u-3/3@s u-mt@s'}
                onLogoClickPath={dashboardPagePath()}
                ariaLabel={t('navigation.logo-aria', { brand: 'DutchFinance' })}
            />
        ) : (
            <NavigationBarShared
                logoAltText={t('navigation.logo-title')}
                navigation={navigation}
                withStaticHeader={withStaticHeader}
                hidden={hidden}
                openMenuText={t('navigation.menu')}
                onLogoClickPath={dashboardPagePath()}
                closeMenuText={t('navigation.close-menu')}
                ariaLabel={t('navigation.logo-aria', { brand: brandsMap[currentBrand] })}
            />
        );

    return NavigationBarSharedWithLogo;
};
